<template>
  <div>
    <h3>{{ _('Scheduled message') }}</h3>
		<div class="row">
			<div class="col-md-6">
				<div class="form-group languageversioned">
					<label for="name">{{ _('Name') }}</label>
					<input class="form-control" type="text" v-model="notification.name" :placeholder="_('Enter a name for the message')" id="notificationName" />
				</div>
			</div>
			<div class="col-md-6">
				<div class="form-group">
					<label for="type">{{ _('Type') }}</label>
					<select class="form-control" id="notificationType" v-model.number="notification.type">
						<option value="1">{{ _('SMS') }}</option>
						<option value="2">{{ _('email') }}</option>
						<option value="3" v-if="allowEmbedded">{{ _('embedded') }}</option>
						<option value="4">{{ _('Zapier') }}</option>
					</select>
				</div>
			</div>
		</div>
    <div class="row" v-if="parseInt(notification.type) === 3">
			<div class="col-xs-12">  
				<div class="form-group">
          <label>{{ _('Tag') }}</label>
          <input type="text" class="form-control" :placeholder="_('Enter a tag that you can use to embed this message into a product notification')" v-model="notification.tag" />
        </div>
      </div>
    </div>
		<div class="row" v-else>
			<div class="col-xs-12">
				<div class="form-group">
					<label for="time">{{ _('Send message') }}</label>
					<div class="input-group">
						<input type="number" data-android-inputmode="decimal" class="form-control" id="notificationTime" v-model.number="notification.time" />
						<div class="input-group-btn">
              <Dropdown buttonClass="btn-default" menuClass="dropdown-menu-right" :teleport="true">
                <template #trigger>
                  {{ timeTypeDesc }} <span class="caret"></span>
                </template>
                <li v-for="(timeTypeDesc, timeType) in types"><a :data-value="timeType" href="javascript:;" v-on:click="selectTimeType(timeType)">{{ _(timeTypeDesc) }}</a></li>
              </Dropdown>
						</div>
					</div>
				</div>
			</div>
		</div>
    <div class="row" v-if="isCustomRecipientSelectorVisible">
      <div class="col-xs-12">
        <div class="form-group">
          <div class="checkbox">
            <label for="useCustom">
              <input type="checkbox" id="useCustom" v-model="notification.use_custom" true-value="1" false-value="0" />
              {{ _('Send only to selected recipients') }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="row" v-if="isCustomEmailSelectorVisible"> 
      <div  class="col-xs-12">
        <div class="form-group">
          <label for="customEmail">{{ _('Custom Email') }}</label>
          <div class="input-group">
            <input type="email" id="customEmail" name="customEmail" class="form-control" v-model="customEmailField" />
            <div class="input-group-btn">
              <button type="button" class="btn btn-default" v-on:click="addCustomEmail">{{ _('Add') }}</button>
            </div>
          </div>
        </div>
        <ul class="list-inline">
          <li v-for="(email, index) in customEmails">{{ email }} <button class="btn btn-sm btn-default" v-on:click="removeCustomEmail(index)">&times;</button></li>
        </ul>
      </div>
    </div>
    <div class="row" v-if="isCustomSMSSelectorVisible">
      <div  class="col-xs-12">
        <div class="form-group">
          <label for="customNumber">{{ _('Custom Number') }}</label>
          <div class="input-group">
            <phone-number-input v-model="customNumberField" :placeholder="_('Enter a custom phone number')" />
            <div class="input-group-btn">
              <button type="button" class="btn btn-default" v-on:click="addCustomNumber">{{ _('Add') }}</button>
            </div>
          </div>
        </div>
        <ul class="list-inline">
          <li v-for="(number, index) in customNumbers">{{ number }} <button class="btn btn-sm btn-default" v-on:click="removeCustomNumber(index)">&times;</button></li>
        </ul>
      </div>
    </div>
		<div class="row">   
			<div class="col-xs-12">  
				<div class="form-group languageversioned"> 
					<label class="message" for="notificationMessage">{{ _('Message') }}</label>
					<textarea class="form-control" id="notificationMessage" v-model="notification.message" rows="8" wrap="virtual"></textarea>
				</div>
			</div>
		</div>
		<div class="row" id="smsPriceWarning" v-if="parseInt(notification.type) === 1">
			<div class="col-xs-12">  
				<p class="text-danger">{{ _('Please note that SMS messages are billed separately, according to the current price list.') }} <a href="javascript:;" v-on:click="showPriceList">{{ _('Price list') }}</a></p>
			</div>
		</div>
    <div class="modal-footer">
      <div class="flex-row space-between">
        <div class="button-col">
          <button class="btn btn-danger pull-left" type="button" v-on:click="remove" v-if="notification.id"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete') }}</span></button>
        </div>
        <div class="button-col">
          <button class="btn btn-default" type="button" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
          <button class="btn btn-success" type="button" v-on:click="save" :disabled="!canSave"><i class="fa fa-fw fa-check"></i> {{ _('Ok') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import modals from '~/lib/modals'
import settingshelpers from '~/lib/settingshelpers'
import { SMSPriceList } from '#components'
import PhoneNumberInput from '~/components/PhoneNumberInput'

export default {
  data () {
    return {
      notification: { ...this.value },
      customEmailField: '',
      customNumberField: '',
    }
  },
  mixins: [modals, settingshelpers],
  props: ['value', 'allowEmbedded', 'settings'],
  computed: {
    types () {
      return {
        'after_order_completed': 'minutes after the order has been completed',
        'after_open_order_completed': 'minutes after the open order has been completed',
        'after_open_order_confirmed': 'minutes after the open order has been confirmed',
        'after_order_processed': 'minutes after the order has been processed',
        'after_order_paid': 'minutes after the order has been paid',
        ...(this.isFeatureEnabled('paymentplans') ? {
          'after_advance_order_completed': 'minutes after the advance payment order has been completed',
          'after_advance_order_processed': 'minutes after the advance payment order has been processed',
          'after_advance_order_paid': 'minutes after the advance payment order has been paid',
        } : {}),
        'before_reservation_starts': 'minutes before the reservation starts',
        'after_reservation_starts': 'minutes after the reservation starts',
        'before_reservation_ends': 'minutes before the reservation ends',
        'after_reservation_ends': 'minutes after the reservation ends',
        'after_checkin': 'minutes after checking in',
        'after_checkout': 'minutes after checking out'
      }
    },
    isCustomRecipientSelectorVisible () {
      return this.notification && this.notification.type && [1, 2].includes(parseInt(this.notification.type))
    },
    isCustomEmailSelectorVisible () {
      return this.isCustomRecipientSelectorVisible && parseInt(this.notification.type) === 2 && parseInt(this.notification.use_custom) === 1
    },
    isCustomSMSSelectorVisible () {
      return this.isCustomRecipientSelectorVisible && parseInt(this.notification.type) === 1 && parseInt(this.notification.use_custom) === 1
    },
    timeType: {
      get () {
        return this.notification && this.notification.timetype ? this.notification.timetype : 'after_order_completed'
      },
      set (newValue) {
        this.notification.timetype = newValue
      }
    },
    timeTypeDesc () {
      return this._(this.types[this.notification.timetype] ? this.types[this.notification.timetype] : this.types['after_order_completed'])
    },
    canSave() {
      if ((this.isCustomEmailSelectorVisible && this.customEmails.length === 0) || (this.isCustomSMSSelectorVisible && this.customNumbers.length === 0)) {
        return false
      }
      return this.notification && this.notification.name && this.notification.type && this.notification.time !== '' && this.notification.message
    },
    customEmails() {
      return this.notification.custom_emails ? this.notification.custom_emails.split(';') : [];
    },
    customNumbers() {
      return this.notification.custom_numbers ? this.notification.custom_numbers.split(';') : [];
    }
  },
  methods: {
    showPriceList() {
      this.showModal('smspricelist', SMSPriceList, {
        props: {},
        events: {},
        handlers: {},
        width: 1000
      })
    },
    selectTimeType(timeType) {
      this.timeType = timeType
    },
    save () {
      if (!this.notification.id) {
        this.notification.id = 'new-' + (new Date().getTime())
      }
      if (!this.notification.timetype) {
        this.notification.timetype = 'after_order_completed'
      }
      this.$emit('save', this.notification)
    },
    close () {
      this.$emit('close')
    },
    remove() {
      this.$emit('remove', this.notification)
    },
    addCustomEmail() {
      if (this.customEmailField) {
        let customEmails = this.notification.custom_emails ? this.notification.custom_emails.split(';') : []
        customEmails.push(this.customEmailField)
        this.customEmailField = ''
        this.notification.custom_emails = customEmails.join(';')
      }
    },
    addCustomNumber() {
      if (this.customNumberField) {
        let customNumbers = this.notification.custom_numbers ? this.notification.custom_numbers.split(';') : []
        customNumbers.push(this.customNumberField)
        this.customNumberField = ''
        this.notification.custom_numbers = customNumbers.join(';')
      }
    },
    removeCustomEmail(index) {
      let customEmails = this.notification.custom_emails ? this.notification.custom_emails.split(';') : []
      customEmails.splice(index, 1)
      this.notification.custom_emails = customEmails.join(';')
    },
    removeCustomNumber(index) {
      let customNumbers = this.notification.custom_numbers ? this.notification.custom_numbers.split(';') : []
      customNumbers.splice(index, 1)
      this.notification.custom_numbers = customNumbers.join(';')
    }
  },
  components: {
    PhoneNumberInput
  }
}
</script>